<script lang="ts">
	/**
	 * The shell every create and edit form lives in.
	 *
	 * Inline forms pushed the list they belonged to off the screen, and on the catalogue panels
	 * they appeared *below* the table — so the admin lost sight of the row they were editing at the
	 * moment they most needed it. A modal keeps the form and its subject in the same view and gives
	 * every one of these screens the same shape, which is the point of doing it in one component
	 * rather than per page.
	 *
	 * It stays a real `<form>`: Enter submits, the submit button is `type="submit"`, and the
	 * browser's own required-field handling still applies. A modal built from a `<div>` and an
	 * on-click handler loses all three, and loses them silently.
	 */

	import { Button, Modal } from 'flowbite-svelte';
	import { createEventDispatcher } from 'svelte';
	import ModalHeader from './ModalHeader.svelte';

	export let open: boolean = false;
	export let title: string;
	/** Font Awesome name, without the `fa-` prefix. */
	export let faIcon: string = 'pen';
	export let submitLabel: string = 'Save';
	/** Disables submission while a request is in flight, and shows why. */
	export let busy: boolean = false;
	/** Set false while the form cannot yet be submitted — a missing required choice, say. */
	export let canSubmit: boolean = true;
	export let size: 'xs' | 'sm' | 'md' | 'lg' | 'xl' = 'md';
	/** Optional line under the title, for a consequence the admin should read before submitting. */
	export let description: string = '';
	/** What the form is about, which is what the titlebar's icon chip is tinted by. */
	export let tone: 'primary' | 'accent' | 'danger' | 'amber' | 'neutral' = 'primary';

	const dispatcher = createEventDispatcher();

	/**
	 * What the dialog itself is showing, as opposed to what the parent asked for.
	 *
	 * Flowbite closes on its own close button, on Escape and on a backdrop click by driving `open`
	 * directly, and most of these forms are opened from a parent's own state — `{#if editing !==
	 * null}` around an `open={true}` — rather than through a binding. Letting flowbite write that
	 * prop therefore closed the *dialog* while leaving the *parent* believing the form was still
	 * up: `editingUUID` stayed set, the `{#if}` stayed true, and every later Edit click set a value
	 * that changed nothing on screen. The screen was not frozen — the edit form simply could never
	 * be opened again, which is what it looks like from the other side.
	 *
	 * So the dialog gets a mirror of its own and the parent keeps the prop. A dismissal moves the
	 * mirror, this component notices, and it goes back out as `cancel` — the one event every one of
	 * these screens already listens for.
	 */
	let dialogOpen: boolean = open;
	$: dialogOpen = open;

	/**
	 * Closing is refused while a request is in flight.
	 *
	 * The write is already on its way; dismissing the form would leave the admin with no idea
	 * whether it landed, and the list behind it not yet refreshed either way.
	 */
	function fnCancel(): void {
		if (busy) return;
		open = false;
		dispatcher('cancel');
	}

	function fnSubmit(): void {
		if (busy || !canSubmit) return;
		dispatcher('submit');
	}

	/*
	 * A dismissal flowbite performed itself, reported to the parent as the cancel it is.
	 *
	 * The veto this replaces was keyed on `busy` alone, which is shared: a panel has one create
	 * form and one edit form and one `saving` flag between them, so saving the edit form made the
	 * create form busy too and the veto read that as a close to refuse — opening a form nobody
	 * asked for. Keying on the dialog's own state cannot make that mistake, because a form that was
	 * never open has nothing to reopen.
	 */
	$: if (!dialogOpen && open) {
		if (busy) {
			dialogOpen = true;
		} else {
			fnCancel();
		}
	}
</script>

<!--
	The title goes in the `header` slot rather than at the top of the form. It used to scroll away
	with the fields under it, so on any form long enough to scroll — which is most of the catalogue
	ones — nothing on screen said which record was being edited.
-->
<Modal bind:open={dialogOpen} {size} outsideclose={!busy} dismissable={!busy} title="">
	<svelte:fragment slot="header">
		<ModalHeader {title} {faIcon} {tone} subtitle={description} />
	</svelte:fragment>

	<form class="space-y-4" on:submit|preventDefault={fnSubmit}>
		<slot />

		<div class="flex flex-wrap items-center gap-3 pt-2">
			<Button type="submit" color="primary" disabled={busy || !canSubmit}>
				{#if busy}<i class="fa-solid fa-spinner fa-spin mr-2" />{/if}{submitLabel}
			</Button>
			<Button color="alternative" disabled={busy} on:click={fnCancel}>Cancel</Button>
			<slot name="actions" />
		</div>
	</form>
</Modal>
